<script>
    import { getTooltipDisplayOptions, vptServer, token, inmateDrawerOpen, selectedDateRange, 
        standardDateRanges, selectedInmateDateArrived, selectedInmateDateDeparted } from '../../stores.js';
    import { hydrateInmate } from '../../inmate.js';
    import { getStartDate, getFinishDate } from '../inmate-graph.js';
    import { getTriggerDatesTooltip } from '../../trigger-date.js';
    import { formatDate, getApiDateFormat } from '../../date-time.js';
    import { isMatching } from "../../string";
    import { sortByProperty } from '../../sort.js';
    import { referenceDataRegimeLevelList, referenceDataCellStatusList } from "../../reference-data.js";

    import Badge from '../../component/Badge.svelte';
    import Dropdown from '../../component/Dropdown.svelte';
    import FilterButton from '../../component/FilterButton.svelte';

    import TableHead from '../../table/TableHead.svelte';
    import TableData from '../../table/TableData.svelte';
    import LocationData from '../../table/LocationData.svelte';

    export let id; 
    export let todaysConfig;

    const YEAR_ON_YEAR = -1;
    const dateRanges = [
        { value : todaysConfig.isolationPastDaysToAnalyse, label : 'Analysis Period (' + todaysConfig.isolationPastDaysToAnalyse + ' days)'},
        ...standardDateRanges,
        { value : 18000, label : 'All'}
    ];

    let history = [];
    let searchCompleted = false;
    let filteredHistory = [];
    let balanceHistory = [];

    let sites = [];
    let keyworkers = [];

    let sortProperty = "date";
    let reverseSorted = true;

    // Filters and controls
    let filterShowHighRiskOnly = false;
    let filterSiteName = "";
    let filterKeyworkerName = "";
    let filterFreeText = "";
    let controlShowHighRiskScoresOnly = false;
    let filterShowRemandedOnly = false;
    let filterShowTrendIncreaseOnly = false;
    let filterShowTriggerDatesOnly = false;

    // A filtered history defined as a reactive declaration. List will be automatically updated when any filters are changed.
    $: {
        history.sort(sortByProperty(sortProperty, reverseSorted));

        filteredHistory = history.filter(function(h) {
            if (filterShowHighRiskOnly && !h.indicatorTargeted) {
                return false; }
            if (filterKeyworkerName != "" && !(filterKeyworkerName == h.keyworkerName)) {
                return false; }
            if (todaysConfig.showSiteName && filterSiteName != "" && !(filterSiteName == h.siteName)) {
                return false; }
            if (filterFreeText != "" && !isMatching(h, filterFreeText.trim())) {
                return false; }
            if (filterShowRemandedOnly && !h.indicatorRemand) {
                return false; }
            if (filterShowTrendIncreaseOnly && h.indicatorTrend != 'U') {
                return false; }
            if (filterShowTriggerDatesOnly && !(h.triggerDateList && h.triggerDateList.length > 0)) {
                return false; }
            return true;
        });
    }

    $: {
        if (todaysConfig) {
            history = [];

            // If the selected isolation date range is not an available option on this view then default to Analysis Period
            if ($selectedDateRange == YEAR_ON_YEAR) {
                selectedDateRange.set(todaysConfig.isolationPastDaysToAnalyse);
            }

            searchCompleted = false;
            getBalanceHistory();
        }
    }

    async function getBalanceHistory() {
        await fetch(`${$vptServer}/inmate/${id}/balance/list?startDate=${getApiDateFormat(getStartDate($selectedDateRange, $selectedInmateDateArrived))}&finishDate=${getApiDateFormat(getFinishDate($selectedInmateDateDeparted))}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            balanceHistory = data;
            getIsolationHistory();
        })
        .catch(error => {
            console.error("Error getting isolation history (balance)");
            console.error(error);
            return;
        });
    }

    async function getIsolationHistory() {
        history = [];

        await fetch(`${$vptServer}/inmate/${id}/isolation/list?startDate=${getApiDateFormat(getStartDate($selectedDateRange, $selectedInmateDateArrived))}&finishDate=${getApiDateFormat(getFinishDate($selectedInmateDateDeparted))}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            data.forEach((i,index,arr) => {
                arr[index] = {...i,
                    ...hydrateInmate(i, i.indicatorTrend, null, $referenceDataRegimeLevelList, $referenceDataCellStatusList, null, null),
                    triggerDateSort: (i.triggerDateList && i.triggerDateList.length > 0),
                    cashBalance: balanceHistory.find(b => b.date === i.date).total };
            });
            history = data;
        })
        .catch(error => {
            console.error("Error getting isolation history");
            console.error(error);
            return;
        });

        keyworkers = [];
        history.forEach((h) => {
            if (h.keyworkerName && !(keyworkers.some(e => e.label === h.keyworkerName))) {
                let keyworker = {value : h.keyworkerName, label : h.keyworkerName}
                keyworkers = [...keyworkers, keyworker];
            }
        });
        keyworkers = keyworkers.sort(sortByProperty("label", false));

        if (keyworkers.filter(k => k.label === filterKeyworkerName).length == 0) {
            filterKeyworkerName = "";
        }

        if (todaysConfig.showSiteName) {
            sites = [];
            filteredHistory.forEach((history) => {
                if (history.siteUrn && !(sites.some(e => e.label === history.siteName))) {
                    let site = {value: history.siteUrn, label: history.siteName}
                    sites = [...sites, site];
                }
            });
            sites = sites.sort(sortByProperty("label", false));
        }

        searchCompleted = true;
    }

    function sort(newSortProperty, reverse) {
        if (newSortProperty === sortProperty) {
            reverseSorted = !reverseSorted;
        }
        else {
            sortProperty = newSortProperty;
            reverseSorted = reverse ? true : false;
        }
    }
</script>

<style>
    .container-fluid {
        padding-top: 163px;
        min-width: 1100px;
    }

    .container-fluid {
        padding-right: 96px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .container-fluid-short {
        padding-right: 270px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .table-filters {
        top: 213px;
    }

    .table.table-card thead {
        top: 261px;
    }
</style>

<div class="container-fluid" class:container-fluid-short={$inmateDrawerOpen}>
    {#if searchCompleted}
        <div class="row table-filters">
            <div class="col-sm-12">
                <div class="form-inline" role="toolbar" aria-label="Table filters">
                    <Dropdown bind:value={$selectedDateRange} id={"date-range"} options={dateRanges} classes={"mr-3"} />
                    
                    {#if history.length > 0}
                        <FilterButton on:click={() => {filterShowHighRiskOnly = !filterShowHighRiskOnly}} classes={"mr-3 btn-img"} id="filter-targeted"
                                isActive={filterShowHighRiskOnly} title="Show dates when the {todaysConfig.inmateLabelName} appeared in the Targeted list">
                            <img src="/assets/filter/target{filterShowHighRiskOnly ? '' : '-inverse'}.svg" alt={"Show only targeted dates"}/>
                        </FilterButton>

                        <FilterButton on:click={() => {filterShowTrendIncreaseOnly = !filterShowTrendIncreaseOnly}} id="filter-trend"
                            isActive={filterShowTrendIncreaseOnly} icon={"arrow-up-short"} iconSize={"lg"} classes={"mr-3"}
                            title="Show only dates where the combined level of isolation has increased compared to the previous day" />

                        <FilterButton on:click={() => {filterShowTriggerDatesOnly = !filterShowTriggerDatesOnly}} id="filter-trigger-dates"
                            isActive={filterShowTriggerDatesOnly} icon={"pin-angle-fill"} classes={"mr-1"}
                            title="Show only dates where there was a {todaysConfig.triggerDatesLabelName.substring(0, todaysConfig.triggerDatesLabelName.length - 1)}" />

                        <FilterButton on:click={() => {filterShowRemandedOnly = !filterShowRemandedOnly}} id="filter-remand" classes={"mr-3"}
                            isActive={filterShowRemandedOnly} icon={"hourglass-split"} 
                            title="Show only dates when the {todaysConfig.inmateLabelName.toLowerCase()} was on Remand" />

                        <FilterButton on:click={()=> {controlShowHighRiskScoresOnly = !controlShowHighRiskScoresOnly}} classes={"mr-auto"}
                            isActive={controlShowHighRiskScoresOnly} icon={"exclamation-octagon"} id ="filter-badge"
                            title={"Show only High Levels"} />

                        {#if keyworkers.length > 0}
                            <Dropdown bind:value={filterKeyworkerName} id={"keyworkers"} options={keyworkers} allLabel={"All " + todaysConfig.keyworkerLabelName + "s"} classes="mr-3"/>
                        {/if}

                        {#if todaysConfig.showSiteName && sites.length > 1}
                            <Dropdown bind:value={filterSiteName} id={"sites"} options={sites} allLabel={"All Sites"} classes="mr-3"/>
                        {/if}

                        <label for="search" class="sr-only">Free Text Search</label>
                        <input type="text" class="form-control" id="search" bind:value={filterFreeText} placeholder="Search..." />
                    {/if}
                </div>
            </div>
        </div>
    {/if}

    <div class="row">
        <div class="col-sm-12">
            {#if searchCompleted}
                {#if filteredHistory.length > 0}
                    <table class="table table-sm table-borderless table-card">
                        <thead>
                            <tr>
                                <TableHead on:click={() => sort("indicatorTargeted", true)} propertyName={"indicatorTargeted"} width={'xs'}
                                        tooltipId={"target-header"} tooltipText={`${todaysConfig.inmateLabelName} is on the Targeted list`} 
                                        {sortProperty} {reverseSorted}>
                                    <img id="target-header" src="/assets/header/target.svg" alt="{todaysConfig.inmateLabelName} is on the Targeted list(s)" />
                                </TableHead>

                                <TableHead on:click={() => sort("trendSort")} propertyName={"trendSort"} icon={"graph-up"}
                                    tooltipText={"Daily Change"} tooltipId={"trend-header"}
                                    {sortProperty} {reverseSorted} invertArrow={true} />

                                <TableHead on:click={() => sort("date", true)} propertyName={"date"} title={"Date"}
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("triggerDateSort", true)} propertyName={"triggerDateSort"} icon={"pin-angle-fill"}
                                    tooltipText={`Recent / Upcoming ${todaysConfig.triggerDatesLabelName}`} tooltipId={"trigger-date-header"} 
                                    {sortProperty} {reverseSorted} />
                           
                                {#if todaysConfig.showCellStatusIcon}
                                    <TableHead on:click={() => sort("cellStatusSort")} propertyName={"cellStatusSort"} icon={"file-person"}
                                        tooltipText="Cell Status" tooltipId={"cell-status-header"}
                                        {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                <TableHead on:click={() => sort("wingCellSort")} propertyName={"wingCellSort"} title={"Cell"}
                                    {sortProperty} {reverseSorted} />
                                    
                                <TableHead on:click={() => sort("indicatorTemporaryDischarged", true)} propertyName={"indicatorTemporaryDischarged"} 
                                    icon={"building-fill"} tooltipText={"Temporary Discharge"} tooltipId={"temp-discharge-header"} 
                                    {sortProperty} {reverseSorted} />

                                {#if !(todaysConfig.isolationVisitWeighting === 0)}
                                    <TableHead on:click={() => sort("levelVisit")} propertyName={"levelVisit"} 
                                        title={todaysConfig.isolationVisitLabel} width={'sm'} center={true}
                                        tooltipText={(todaysConfig.isolationVisitDescription && !(todaysConfig.isolationVisitLabel == todaysConfig.isolationVisitDescription)) ? todaysConfig.isolationVisitDescription : null}
                                        tooltipId={"visits-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !(todaysConfig.isolationWorkWeighting === 0)}
                                    <TableHead on:click={() => sort("levelWork")} propertyName={"levelWork"} 
                                        title={todaysConfig.isolationWorkLabel} width={'sm'} center={true}
                                        tooltipText={(todaysConfig.isolationWorkDescription && !(todaysConfig.isolationWorkLabel == todaysConfig.isolationWorkDescription)) ? todaysConfig.isolationWorkDescription : null}
                                        tooltipId={"work-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !(todaysConfig.isolationPhoneWeighting === 0)}
                                    <TableHead on:click={() => sort("levelPhone")} propertyName={"levelPhone"} 
                                        title={todaysConfig.isolationPhoneLabel} width={'sm'} center={true}
                                        tooltipText={(todaysConfig.isolationPhoneDescription && !(todaysConfig.isolationPhoneLabel == todaysConfig.isolationPhoneDescription)) ? todaysConfig.isolationPhoneDescription : null}
                                        tooltipId={"phone-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !(todaysConfig.isolationMealWeighting === 0)}
                                    <TableHead on:click={() => sort("levelMeal")} propertyName={"levelMeal"} 
                                        title={todaysConfig.isolationMealLabel} width={'sm'} center={true}
                                        tooltipText={(todaysConfig.isolationMealDescription && !(todaysConfig.isolationMealLabel == todaysConfig.isolationMealDescription)) ? todaysConfig.isolationMealDescription : null}
                                        tooltipId={"meals-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !(todaysConfig.isolationCanteenWeighting === 0)}
                                    <TableHead on:click={() => sort("levelCanteen")} propertyName={"levelCanteen"} 
                                        title={todaysConfig.isolationCanteenLabel} width={'sm'} center={true}
                                        tooltipText={(todaysConfig.isolationCanteenDescription && !(todaysConfig.isolationCanteenLabel == todaysConfig.isolationCanteenDescription)) ? todaysConfig.isolationCanteenDescription : null}
                                        tooltipId={"canteen-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !(todaysConfig.isolationGymWeighting === 0)}
                                    <TableHead on:click={() => sort("levelGym")} propertyName={"levelGym"}
                                        title={todaysConfig.isolationGymLabel} width={'sm'} center={true}
                                        tooltipText={(todaysConfig.isolationGymDescription && !(todaysConfig.isolationGymLabel == todaysConfig.isolationGymDescription)) ? todaysConfig.isolationGymDescription : null}
                                        tooltipId={"gym-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !(todaysConfig.isolationPaWeighting === 0)}
                                    <TableHead on:click={() => sort("levelActivity")} propertyName={"levelActivity"}
                                        title={todaysConfig.isolationPaLabel} width={'sm'} center={true}
                                        tooltipText={(todaysConfig.isolationPaDescription && !(todaysConfig.isolationPaLabel == todaysConfig.isolationPaDescription)) ? todaysConfig.isolationPaDescription : null}
                                        tooltipId={"activities-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                <TableHead on:click={() => sort("regimeLevelDescription")} propertyName={"regimeLevelDescription"} 
                                    title={"Regime"} {sortProperty} {reverseSorted} classes={"table-pad-left"} />

                                <TableHead on:click={() => sort("cashBalance")} propertyName={"cashBalance"} title={"Balance"} 
                                    {sortProperty} {reverseSorted} />
                                        
                                <TableHead on:click={() => sort("indicatorRemand", true)} propertyName={"indicatorRemand"} icon={"hourglass-split"}
                                    tooltipText={`${todaysConfig.inmateLabelName} is on remand`} tooltipId={"remand-header"} 
                                    {sortProperty} {reverseSorted} />
                                
                                <TableHead on:click={() => sort("status")} propertyName={"status"} 
                                    title={"Status"} {sortProperty} {reverseSorted} classes="hidden-col-lv-2" />

                                {#if keyworkers.length > 0}
                                    <TableHead on:click={() => sort("keyworkerName")} propertyName={"keyworkerName"} 
                                        title={todaysConfig.keyworkerLabelName} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.showSiteName && sites.length > 1}
                                    <TableHead on:click={() => sort("siteName")} propertyName={"siteName"}
                                        title={"Site"} {sortProperty} {reverseSorted} classes="hidden-col-lv-1" />
                                {/if}
                            </tr>
                        </thead>

                        <tbody>
                            {#each filteredHistory as history (history.vptHeaderUrn)}
                                <tr>
                                    <!-- Targeted Indicator -->
                                    <TableData display={history.indicatorTargeted} classes="table-text-center" tooltipId={`targeted-indicator-${history.vptHeaderUrn}`}
                                            tooltipText={`${todaysConfig.inmateLabelName} was targeted on this date`}>
                                        <img id="targeted-indicator-{history.vptHeaderUrn}" src="/assets/target-indicator.svg" alt="{todaysConfig.inmateLabelName} was targeted on this date" />    
                                    </TableData>
                                    
                                    <!-- Trend Indicator -->
                                    <TableData display={history.indicatorTrend == 'U' || history.indicatorTrend == 'D'}
                                        icon={history.indicatorTrend == 'U' ? "arrow-up-short" : "arrow-down-short"} iconSize={'xl'}
                                        tooltipText={`Combined level of isolation has ${history.indicatorTrend == 'U' ? 'increased' : 'decreased'} compared to the previous day`}
                                        tooltipId={`trend-indicator-${history.vptHeaderUrn}`} classes={history.indicatorTrend == 'U' ? "text-danger" : "text-success"}/>

                                    <!-- Date -->
                                    <TableData text={formatDate(history.date)} />

                                    <!-- Trigger Date Indicator -->
                                    <TableData display={history.triggerDateList && history.triggerDateList.length > 0} icon={"pin-angle-fill"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ align: "left", maxWidth: 500 })}
                                        tooltipText={getTriggerDatesTooltip(history.triggerDateList)} />

                                    <!-- Cell Status -->
                                    {#if todaysConfig.showCellStatusIcon}
                                        <TableData display={history.vptCellStatusUrn > 0} tooltipId={`cell-status-data-${history.vptHeaderUrn}`} 
                                                tooltipText={history.cellStatusDescription} center={true}>
                                            <img id="cell-status-data-{history.vptHeaderUrn}" src="/assets/{history.cellStatusIcon}.svg" alt="{history.cellStatusDescription}" />
                                        </TableData>
                                    {/if}

                                    <!-- Cell Location -->
                                    <LocationData inmateUrn={history.vptHeaderUrn} unit={history.unit} 
                                        wing={history.wing} cell={history.cell} />
                                    
                                    <!-- Temporary Discharge Indicator -->
                                    <TableData display={history.indicatorTemporaryDischarged} icon={"building-fill-x"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ maxWidth: 500 })}
                                        tooltipText={{tooltipLines: [history.dischargedTo]}} />

                                    <!-- Visits -->
                                    {#if !(todaysConfig.isolationVisitWeighting === 0)}
                                        <TableData center={true}>
                                            <Badge id="level-visit-{history.vptHeaderUrn}" score={history.levelVisit} showLabel={false}
                                            showBadge={!controlShowHighRiskScoresOnly} lastDate={history.dateLastVisit}
                                            changeDate={history.dateLevelLastChangedVisit} attendedLabel={"Visited"} />
                                        </TableData>
                                    {/if}
                                    
                                    <!-- Work -->
                                    {#if !(todaysConfig.isolationWorkWeighting === 0)}
                                        <TableData center={true}>
                                            <Badge id="level-work-{history.vptHeaderUrn}" score={history.levelWork} showLabel={false}
                                            showBadge={!controlShowHighRiskScoresOnly} lastDate={history.dateLastWork}
                                            changeDate={history.dateLevelLastChangedWork} />
                                        </TableData>
                                    {/if}
                                    
                                    <!-- Phone -->
                                    {#if !(todaysConfig.isolationPhoneWeighting === 0)}
                                        <TableData center={true}>
                                            <Badge id="level-phone-{history.vptHeaderUrn}" score={history.levelPhone} showLabel={false}
                                            showBadge={!controlShowHighRiskScoresOnly} lastDate={history.dateLastPhone}
                                            changeDate={history.dateLevelLastChangedPhone} attendedLabel="Top Up" />
                                        </TableData>
                                    {/if}
                                    
                                    <!-- Meals -->
                                    {#if !(todaysConfig.isolationMealWeighting === 0)}
                                        <TableData center={true}>
                                            <Badge id="level-meal-{history.vptHeaderUrn}" score={history.levelMeal} showLabel={false}
                                            showBadge={!controlShowHighRiskScoresOnly} lastDate={history.dateLastMeal}
                                            changeDate={history.dateLevelLastChangedMeal} attendedLabel="Selected" />
                                        </TableData>
                                    {/if}
                                    
                                    <!-- Canteen -->
                                    {#if !(todaysConfig.isolationCanteenWeighting === 0)}
                                        <TableData center={true}>
                                            <Badge id="level-canteen-{history.vptHeaderUrn}" score={history.levelCanteen} showLabel={false}
                                            showBadge={!controlShowHighRiskScoresOnly} lastDate={history.dateLastCanteen}
                                            changeDate={history.dateLevelLastChangedCanteen} attendedLabel="Purchased"
                                            additionalTooltipText={history.levelCanteen < 3 ? `${todaysConfig.inmateLabelName} spent ${history.levelCanteen == 1 ? "significantly" : ""} less than their 7 day average` : null} />
                                        </TableData>
                                    {/if}
                                    
                                    <!-- Gym -->
                                    {#if !(todaysConfig.isolationGymWeighting === 0)}
                                        <TableData center={true}>
                                            <Badge id="level-gym-{history.vptHeaderUrn}" score={history.levelGym} showLabel={false}
                                            showBadge={!controlShowHighRiskScoresOnly} lastDate={history.dateLastGym}
                                            changeDate={history.dateLevelLastChangedGym} />
                                        </TableData>
                                    {/if}
                                    
                                    <!-- Other Activities -->
                                    {#if !(todaysConfig.isolationPaWeighting === 0)}
                                        <TableData center={true}>
                                            <Badge id="level-activity-{history.vptHeaderUrn}" score={history.levelActivity} showLabel={false}
                                            showBadge={!controlShowHighRiskScoresOnly} lastDate={history.dateLastActivity}
                                            changeDate={history.dateLevelLastChangedActivity} />
                                        </TableData>
                                    {/if}

                                    <!-- Regime -->
                                    <TableData display={history.regimeLevelDescription} text={history.regimeLevelDescription}
                                        classes="table-pad-left" tooltipId={`regime-data-${history.vptHeaderUrn}`}
                                        tooltipText={history.regimeStartDate ? `Since ${formatDate(history.regimeStartDate)}` : null} />

                                    <!-- Balance -->
                                    <TableData display={history.cashBalance != null} text={`£${history.cashBalance ? history.cashBalance.toFixed(2) : '0'}`} />

                                    <!-- Remand Indicator -->
                                    <TableData display={history.indicatorRemand} icon={"hourglass-split"} />

                                    <!-- Status -->
                                    <TableData display={history.status} text={history.status} />

                                    <!-- Keyworker -->
                                    {#if keyworkers.length > 0}
                                        <TableData display={history.keyworkerName} text={history.keyworkerName} tooltipId={`keyworker-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.keyworkerStartDate ? `Since ${formatDate(history.keyworkerStartDate)}` : null} />
                                    {/if}

                                    <!-- Site -->
                                    {#if todaysConfig.showSiteName && sites.length > 1}
                                        <TableData display={history.siteName} text={history.siteName} classes="hidden-col-lv-1" />
                                    {/if}
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                {:else}
                    {#if history.length === 0}
                        <div class="alert alert-danger" role="alert">No Isolation data was found for the selected date period.</div>
                    {:else}
                        <div class="alert alert-warning" role="alert">No Isolation data is currently being displayed. Please check the selected filters.</div>
                    {/if}
                {/if}
            {:else}
                <div class="spinner-container">
                    <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
                    <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
                </div>
            {/if}
        </div>
    </div>
</div>